import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent, subscribeCookieConsent } from '../lib/cookieConsent';

/**
 * FacebookPixel - Meta Pixel for tracking and ads
 * 
 * Setup:
 * 1. Go to business.facebook.com/events_manager
 * 2. Create a Pixel
 * 3. Add to .env: VITE_FB_PIXEL_ID=123456789
 * 
 * Features:
 * - Automatic PageView tracking on route change
 * - Standard events ready (ViewContent, AddToCart, Purchase)
 * - Only loads when Pixel ID is configured
 */

// Get Pixel ID from environment
const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID;

// Declare fbq function type
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}

// Initialize Facebook Pixel (after consent).
const initPixel = () => {
  if (typeof window.fbq === 'function') return;

  const n = (window.fbq = function (...args: unknown[]) {
    if ((n as unknown as { callMethod?: (...args: unknown[]) => void }).callMethod) {
      (n as unknown as { callMethod: (...args: unknown[]) => void }).callMethod(...args);
    } else {
      (n as unknown as { queue: unknown[] }).queue.push(args);
    }
  });

  if (!window._fbq) window._fbq = n;
  (n as unknown as { push: typeof n }).push = n;
  (n as unknown as { loaded: boolean }).loaded = true;
  (n as unknown as { version: string }).version = '2.0';
  (n as unknown as { queue: unknown[] }).queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq('init', FB_PIXEL_ID);
  window.fbq('track', 'PageView');
};

const loadIfConsented = () => {
  if (hasAnalyticsConsent() && FB_PIXEL_ID) {
    initPixel();
  }
};

/**
 * FacebookPixel Component
 * Add this once in your app (e.g., in Layout)
 */
export function FacebookPixel() {
  const location = useLocation();

  // Load Pixel only after visitor accepts analytics cookies (PIPEDA).
  useEffect(() => {
    loadIfConsented();
    return subscribeCookieConsent(() => loadIfConsented());
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (FB_PIXEL_ID && hasAnalyticsConsent() && window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);

  // Don't render anything visible
  return null;
}

/**
 * Track custom events
 * 
 * @example
 * fbTrack('ViewContent', { content_name: 'Chocolate Cake', value: 45.00 })
 */
export function fbTrack(
  eventName: string,
  params?: Record<string, unknown>
) {
  if (FB_PIXEL_ID && hasAnalyticsConsent() && window.fbq) {
    window.fbq('track', eventName, params);
  }
}

/**
 * Track product view
 */
export function fbTrackViewContent(item: {
  id: string;
  name: string;
  category?: string;
  price?: number;
}) {
  fbTrack('ViewContent', {
    content_ids: [item.id],
    content_name: item.name,
    content_category: item.category || 'Bakery',
    content_type: 'product',
    value: item.price || 0,
    currency: 'CAD',
  });
}

/**
 * Track add to cart
 */
export function fbTrackAddToCart(item: {
  id: string;
  name: string;
  price: number;
  quantity?: number;
}) {
  fbTrack('AddToCart', {
    content_ids: [item.id],
    content_name: item.name,
    content_type: 'product',
    value: item.price * (item.quantity || 1),
    currency: 'CAD',
  });
}

/**
 * Track purchase/order
 */
export function fbTrackPurchase(order: {
  value: number;
  items: Array<{ id: string; name: string; price: number; quantity: number }>;
}) {
  fbTrack('Purchase', {
    content_ids: order.items.map(i => i.id),
    content_type: 'product',
    value: order.value,
    currency: 'CAD',
    num_items: order.items.reduce((sum, i) => sum + i.quantity, 0),
  });
}

/**
 * Track contact/lead
 */
export function fbTrackContact() {
  fbTrack('Contact');
}

/**
 * Track when someone initiates checkout
 */
export function fbTrackInitiateCheckout(value: number, numItems: number) {
  fbTrack('InitiateCheckout', {
    value,
    currency: 'CAD',
    num_items: numItems,
  });
}

export default FacebookPixel;

